import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuditLogService, AuditLogEntry } from 'src/app/_share/_services/audit-log.service';
import { WebSocketService, SystemUpdate } from 'src/app/_share/_services/websocket.service';
import { NotificationService } from 'src/app/_share/_services/notification.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuditLogDialogComponent } from 'src/app/features/system/components/audit-log-dialog/audit-log-dialog.component';

@Component({
  selector: 'app-share-audit-logs',
  templateUrl: './share-audit-logs.component.html',
  styleUrls: ['./share-audit-logs.component.scss']
})
export class ShareAuditLogsComponent implements OnInit, OnDestroy {
  auditLogs: AuditLogEntry[] = [];
  loading: boolean = false;
  private destroy$ = new Subject<void>();
  private dialogRef: DynamicDialogRef | undefined;

  @Input() contexts: string[] = ['system', 'user_management', 'portal_management', 'portal'];
  @Input() logCount: number = 10;
  // When set, "View Full Log" navigates here instead of opening the dialog -
  // used by pages that have their own dedicated full-page audit log view.
  @Input() viewFullLogRoute?: string;

  constructor(
    private auditLogService: AuditLogService,
    private webSocketService: WebSocketService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAuditLogSummary();
    this.subscribeToRealTimeUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToRealTimeUpdates(): void {
    this.webSocketService
      .subscribeToSystemUpdates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((update: SystemUpdate | null) => {
        if (update && update.entity === 'audit_log') {
          this.handleAuditLogUpdate(update);
        }
      });
  }

  private handleAuditLogUpdate(update: SystemUpdate): void {
    const data = update.data as Partial<AuditLogEntry>;

    switch (update.action) {
      case 'created':
        if (data) {
          // If context filtering is applied, ensure the new log matches the context
          for (let context of this.contexts) {
            if (data.context?.toLowerCase() === context.toLowerCase()) {
              this.auditLogs.unshift(data as AuditLogEntry);
              if (this.auditLogs.length > this.logCount) {
                this.auditLogs = this.auditLogs.slice(0, this.logCount);
              }
              return; // Exit after adding the log
            }
          }
        }
        break;
      case 'updated':
        if (data && data.id) {
          const normalizedContexts = this.contexts.map((ctx) => ctx.toLowerCase());
          const matchesContext =
            normalizedContexts.length === 0 ||
            (data.context ? normalizedContexts.includes(data.context.toLowerCase()) : false);

          const index = this.auditLogs.findIndex((log) => log.id === data.id);

          if (!matchesContext) {
            if (index !== -1) {
              this.auditLogs.splice(index, 1);
            }
            return;
          }

          if (index !== -1) {
            this.auditLogs[index] = {
              ...this.auditLogs[index],
              ...data
            } as AuditLogEntry;
          }
        }
        break;
      case 'deleted':
        if (data && data.id) {
          this.auditLogs = this.auditLogs.filter((log) => log.id !== data.id);
        }
        break;
    }
  }

  loadAuditLogSummary(): void {
    this.loading = true;
    this.auditLogService
      .getAuditLogSummary({
        limit: this.logCount,
        offset: 0,
        q: '',
        context: this.contexts
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Raw logs from summary API: ', response.data);

          // filter response based on context if needed
          this.contexts = this.contexts.map((ctx) => ctx.toLowerCase());
          if (this.contexts.length > 0) {
            this.auditLogs = response.data.filter((log) =>
              log.context ? this.contexts.includes(log.context.toLowerCase()) : false
            );
            this.loading = false;
          } else {
            this.auditLogs = response.data;
            this.loading = false;
          }
          console.log('logs after loading summary: ', this.auditLogs);
        },
        error: (error) => {
          this.loading = false;
          this.auditLogs = [];

          if (error.status !== 401 && error.status !== 403) {
            this.notificationService.showError(
              'Loading Audit Logs Failed',
              'An error occurred while loading audit logs.'
            );
          }
        }
      });
  }

  getSeverityColor(actionType: string): string {
    switch (actionType) {
      case 'CREATE':
        return 'bg-green-500';
      case 'UPDATE':
        return 'bg-blue-500';
      case 'DELETE':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  getTimeAgo(timestamp: string): string {
    const now = new Date();
    const logTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - logTime.getTime()) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute(s) ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour(s) ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }

  getActionDisplayName(log: AuditLogEntry): string {
    return log.action_description || log.action_type || 'Unknown Action';
  }

  getActionCategory(log: AuditLogEntry): string {
    return log.entity_type || log.action_type || 'System';
  }

  viewFullAuditLog(): void {
    if (this.viewFullLogRoute) {
      this.router.navigate([this.viewFullLogRoute]);
      return;
    }

    this.dialogRef = this.dialogService.open(AuditLogDialogComponent, {
      header: 'Full Audit Log',
      width: '95vw',
      // height: '90vh',
      showHeader: false,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      data: {
        context_data: this.contexts
      }
    });

    this.dialogRef.onClose.subscribe((result: unknown) => {
      if (result) {
        // Handle any result from the dialog if needed
        console.log('Dialog closed with result:', result);
      }
    });
  }
}
